import React, { useState, useEffect, useMemo } from 'react';
import { Wrench, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { puntovService } from '../../../services/api';
import Modal from './Modal';

// Lo que se marca solo al abrir: renombrar y quitar filas idénticas no
// pierden datos. Crear tablas vacías se elige a mano, máquina por máquina.
const accionesSeguras = (m) => [
  ...(m.renombrar.length ? ['renombrar'] : []),
  ...(m.duplicados?.identicas ? ['quitar_duplicados'] : [])
];

const reparable = (m) => !m.conflicto && (m.renombrar.length > 0 || m.crear.length > 0 || Boolean(m.duplicados?.identicas));

const Opcion = ({ marcada, onChange, titulo, children, aviso = false }) => (
  <label className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer ${marcada ? (aviso ? 'border-amber-300 bg-amber-50' : 'border-blue-300 bg-blue-50') : 'border-gray-200 hover:bg-gray-50'}`}>
    <input type="checkbox" checked={marcada} onChange={onChange} className="mt-0.5" />
    <span className="min-w-0">
      <span className="block text-sm text-gray-800">{titulo}</span>
      <span className="block text-xs text-gray-500 font-mono break-all">{children}</span>
    </span>
  </label>
);

// Repara las máquinas de una base de Punto V: tablas con otras mayúsculas,
// tablas faltantes y filas repetidas en db_maquinas. Punto V recalcula el plan
// al aplicar y rechaza lo que ya no sea seguro.
const DialogoRepararMaquinas = ({ base, onClose, onReparado }) => {
  const [maquinas, setMaquinas] = useState([]);
  const [elegidas, setElegidas] = useState({});
  const [cargando, setCargando] = useState(true);
  const [reparando, setReparando] = useState(false);
  const [error, setError] = useState('');
  const [resultados, setResultados] = useState(null);

  useEffect(() => {
    let vigente = true;
    puntovService.problemasMaquinas(base)
      .then((res) => {
        if (!vigente) return;
        const lista = res.data || [];
        setMaquinas(lista);
        setElegidas(Object.fromEntries(lista.map(m => [m.id_maquina, accionesSeguras(m)])));
      })
      .catch((err) => vigente && setError(err.message || 'No se pudieron revisar las máquinas'))
      .finally(() => vigente && setCargando(false));
    return () => { vigente = false; };
  }, [base]);

  const alternar = (id, accion) => setElegidas(prev => {
    const actuales = prev[id] || [];
    return { ...prev, [id]: actuales.includes(accion) ? actuales.filter(a => a !== accion) : [...actuales, accion] };
  });

  const conCrear = maquinas.filter(m => !m.conflicto && m.crear.length);
  const todasCrear = conCrear.length > 0 && conCrear.every(m => elegidas[m.id_maquina]?.includes('crear'));
  const alternarCrearTodas = () => setElegidas(prev => {
    const nuevo = { ...prev };
    conCrear.forEach(m => {
      const sinCrear = (nuevo[m.id_maquina] || []).filter(a => a !== 'crear');
      nuevo[m.id_maquina] = todasCrear ? sinCrear : [...sinCrear, 'crear'];
    });
    return nuevo;
  });

  const pedido = useMemo(() => Object.entries(elegidas)
    .filter(([, acciones]) => acciones.length)
    .map(([id_maquina, acciones]) => ({ id_maquina, acciones })), [elegidas]);
  const tablasVacias = pedido.reduce((n, p) => n + (p.acciones.includes('crear') ? 1 : 0), 0);

  const reparar = async () => {
    setError('');
    setReparando(true);
    try {
      const res = await puntovService.repararMaquinas(base, pedido);
      setResultados(res.data?.resultados || []);
      onReparado();
    } catch (err) {
      setError(err.message || 'No se pudieron reparar las máquinas');
    } finally {
      setReparando(false);
    }
  };

  const pie = resultados ? (
    <button type="button" onClick={onClose} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">Cerrar</button>
  ) : (
    <>
      <button type="button" onClick={onClose} disabled={reparando} className="px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100">Cancelar</button>
      <button
        type="button"
        onClick={reparar}
        disabled={reparando || cargando || !pedido.length}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
      >
        <Wrench size={15} /> {reparando ? 'Reparando…' : `Reparar ${pedido.length} máquina(s)`}
      </button>
    </>
  );

  return (
    <Modal titulo="Reparar máquinas" subtitulo={base} icono={Wrench} onClose={onClose} bloqueado={reparando} ancho="lg" pie={pie}>
      {cargando && <div className="text-center py-8"><div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>}

      {!cargando && !resultados && maquinas.length === 0 && !error && (
        <p className="text-sm text-gray-500">Las máquinas de esta base ya no tienen problemas.</p>
      )}

      {!cargando && !resultados && maquinas.length > 0 && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
            <span>Elige qué reparar en cada máquina. Lo que no pierde datos ya viene marcado.</span>
            {conCrear.length > 1 && (
              <button type="button" onClick={alternarCrearTodas} className="text-xs text-blue-700 hover:underline">
                {todasCrear ? 'Desmarcar crear en todas' : `Crear tablas en las ${conCrear.length}`}
              </button>
            )}
          </div>

          {maquinas.map(m => {
            const elegida = elegidas[m.id_maquina] || [];
            return (
              <div key={m.id_maquina} className={`rounded-xl border p-3 space-y-2 ${reparable(m) ? 'border-gray-200' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-mono text-sm font-medium text-gray-800">{m.id_maquina}</span>
                  <span className="text-xs text-gray-500">{m.tipo_maq}{m.descripcion ? ` · ${m.descripcion}` : ''}</span>
                </div>

                {m.conflicto && (
                  <p className="text-xs text-red-700 flex items-start gap-1"><XCircle size={13} className="mt-0.5 flex-shrink-0" /> {m.conflicto}</p>
                )}

                {!m.conflicto && m.renombrar.length > 0 && (
                  <Opcion marcada={elegida.includes('renombrar')} onChange={() => alternar(m.id_maquina, 'renombrar')} titulo="Renombrar sus tablas con las mayúsculas correctas (conserva las ventas)">
                    {m.renombrar.map(r => `${r.de} → ${r.a}`).join(' · ')}
                  </Opcion>
                )}

                {!m.conflicto && m.crear.length > 0 && (
                  <Opcion aviso marcada={elegida.includes('crear')} onChange={() => alternar(m.id_maquina, 'crear')} titulo={`Crear ${m.crear.length === 1 ? 'la tabla que falta' : `las ${m.crear.length} tablas que faltan`}, vacías`}>
                    {m.crear.join(' · ')}
                  </Opcion>
                )}

                {!m.conflicto && m.duplicados?.identicas && (
                  <Opcion marcada={elegida.includes('quitar_duplicados')} onChange={() => alternar(m.id_maquina, 'quitar_duplicados')} titulo={`Quitar ${m.duplicados.filas - 1} fila(s) repetida(s) de db_maquinas`}>
                    Las {m.duplicados.filas} filas son idénticas; queda una sola
                  </Opcion>
                )}

                {m.duplicados && !m.duplicados.identicas && (
                  <p className="text-xs text-gray-500">
                    Registrada {m.duplicados.filas} veces con datos distintos: hay que decidir a mano cuál fila vale.
                  </p>
                )}
              </div>
            );
          })}

          {tablasVacias > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-xs flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span>
                Vas a crear tablas vacías en {tablasVacias} máquina(s): empiezan sin historial de ventas y con las posiciones de su tipo sin producto.
                Si sus ventas están en otra base, no las crees: primero hay que traer esas tablas.
              </span>
            </div>
          )}
        </div>
      )}

      {resultados && (
        <ul className="space-y-2">
          {resultados.map(r => (
            <li key={r.id_maquina} className={`rounded-lg border p-3 text-sm ${r.ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <div className={`flex items-center gap-1.5 font-medium ${r.ok ? 'text-green-800' : 'text-red-800'}`}>
                {r.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                <span className="font-mono">{r.id_maquina}</span>
              </div>
              {r.hechos?.length > 0 && <ul className="mt-1 text-xs text-gray-700 list-disc pl-5">{r.hechos.map((h, i) => <li key={i}>{h}</li>)}</ul>}
              {r.error && <p className="mt-1 text-xs text-red-700">{r.error}</p>}
            </li>
          ))}
        </ul>
      )}

      {error && <div className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
    </Modal>
  );
};

export default DialogoRepararMaquinas;
